import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdModal from "../ads/AdModal";

function ManageComment({ onAdd, onDelete }) {
  const { token, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdModal, setShowAdModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [adLoadingId, setAdLoadingId] = useState(null);
  const { t } = useTranslation();

  const URL = useMemo(
    () =>
      `${process.env.REACT_APP_BACKEND_URL}/ad_comments.php?my=1&page=${page}&limit=${limit}`,
    [page, limit]
  );

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(URL, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        if (!mounted) return;
        const data = res?.data?.data || [];
        setItems(data);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load comments"
        );
        if (err?.response?.status === 401) {
          navigate("/login");
        }
      } finally {
        mounted && setLoading(false);
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [URL, token, navigate]);

  const handleViewAd = async (adId) => {
    if (!adId) return;
    setAdLoadingId(adId);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/ads.php`,
        {
          params: { id: adId },
          withCredentials: true,
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        }
      );
      const ad = res?.data?.ad || null;
      if (ad) {
        setSelectedAd(ad);
        setShowAdModal(true);
      }
    } catch (e) {
      // optional toast or inline error could be added
    } finally {
      setAdLoadingId(null);
    }
  };

  const handleCloseAdModal = () => {
    setShowAdModal(false);
    setSelectedAd(null);
  };

  return (
    <div className="comment-manager p-4 bg-slate-50 rounded-lg">
      <h3 className="font-bold mb-2">
        {t("myComments", { defaultValue: "My Comments" })}
      </h3>
      {loading && <div className="text-sm text-slate-500">{t("loading")}</div>}
      {error && (
        <div className="text-sm text-red-600 mb-2">
          {t("error", { defaultValue: "Error" })}: {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="text-sm text-slate-500">{t("noComments")}</div>
      )}
      <ul className="divide-y divide-slate-200">
        {items.map((c) => (
          <li key={c.id} className="py-3">
            <div className="flex flex-col gap-1">
              <div className="text-slate-800">{c.content}</div>
              <div className="text-xs text-slate-500">
                <span className="mr-2">
                  {new Date(c.created_at).toLocaleString()}
                </span>
                {c.ad_id && (
                  <button
                    type="button"
                    className="text-blue-600 hover:underline disabled:opacity-50"
                    onClick={() => handleViewAd(c.ad_id)}
                    disabled={!!adLoadingId}
                    title={adLoadingId === c.ad_id ? t("loading") : undefined}
                  >
                    {adLoadingId === c.ad_id
                      ? t("loading")
                      : t("viewAd", { defaultValue: "View Ad" })}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 pt-3">
        <button
          className="px-2 py-1 bg-white border rounded disabled:opacity-50"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
        >
          {t("prevPage", { defaultValue: "Previous" })}
        </button>
        <div className="text-sm text-slate-600 self-center">
          {t("pageLabel", { page, defaultValue: `Page ${page}` })}
        </div>
        <button
          className="px-2 py-1 bg-white border rounded disabled:opacity-50"
          onClick={() => setPage((p) => p + 1)}
          disabled={loading || items.length < limit}
        >
          {t("nextPage", { defaultValue: "Next" })}
        </button>
      </div>
      {/* Ad modal for viewing ad info */}
      <AdModal
        show={showAdModal}
        ad={selectedAd}
        onClose={handleCloseAdModal}
        user={user}
        token={token}
        t={t}
        type={selectedAd?.adType || "tutor"}
      />
    </div>
  );
}

export default ManageComment;
